import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Col, Form, Image, InputGroup, Modal, Row, Table, Nav, Alert, Spinner, Card, Tabs, Tab, ProgressBar } from "react-bootstrap";
import { oneKlicks } from '../../SDK/oneclick';
import ImageShow from '../../Uitiles/ImageShow';
import { toast } from 'react-toastify';

const UserActivityTracking = () => {
    // State management
    const [activeTab, setActiveTab] = useState('overview');
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showActivityModal, setShowActivityModal] = useState(false);
    
    // Data states
    const [employeesList, setEmployeesList] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [screenshots, setScreenshots] = useState([]);
    const [timeTracking, setTimeTracking] = useState([]);
    const [productivityMetrics, setProductivityMetrics] = useState([]);
    
    // Filter states
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
    const [timeRange, setTimeRange] = useState('today');

    useEffect(() => {
        fetchEmployeesList();
        if (selectedEmployee) {
            fetchEmployeeActivity(selectedEmployee.id);
        }
    }, [selectedEmployee, dateFilter, timeRange]);

    // Fetch employees list
    const fetchEmployeesList = async () => {
        setLoading(true);
        try {
            const tableSchema = '';
            const tableName = 'employees';
            const conditions = { status: 'active' };
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'users',
                    target_column: 'id',
                    target_value: 'staff_id',
                    target_label: 'staff'
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setEmployeesList(response.data);
            } else {
                toast.error('Failed to fetch employees list');
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast.error('Error fetching employees list');
        } finally {
            setLoading(false);
        }
    };

    // Fetch employee activity data
    const fetchEmployeeActivity = async (employeeId) => {
        try {
            // Fetch activity logs
            await fetchActivityLogs(employeeId);
            // Fetch screenshots
            await fetchScreenshots(employeeId);
            // Fetch time tracking
            await fetchTimeTracking(employeeId);
            // Fetch productivity metrics
            await fetchProductivityMetrics(employeeId);
        } catch (error) {
            console.error('Error fetching employee activity:', error);
            toast.error('Error fetching employee activity data');
        }
    };

    // Fetch activity logs
    const fetchActivityLogs = async (employeeId) => {
        try {
            const tableSchema = '';
            const tableName = 'user_activity_logs';
            const conditions = { employee_id: employeeId };
            const orderBy = 'created_at DESC';
            const search = {};
            const pagination = {};

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination);
            
            if (response.success) {
                setActivityLogs(response.data);
            }
        } catch (error) {
            console.error('Error fetching activity logs:', error);
        }
    };

    // Fetch screenshots
    const fetchScreenshots = async (employeeId) => {
        try {
            const tableSchema = '';
            const tableName = 'user_screenshots';
            const conditions = { employee_id: employeeId };
            const orderBy = 'captured_at DESC';
            const search = {};
            const pagination = {};

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination);
            
            if (response.success) {
                setScreenshots(response.data);
            }
        } catch (error) {
            console.error('Error fetching screenshots:', error);
        }
    };

    // Fetch time tracking
    const fetchTimeTracking = async (employeeId) => {
        try {
            const tableSchema = '';
            const tableName = 'user_time_tracking';
            const conditions = { employee_id: employeeId };
            const orderBy = 'date DESC';
            const search = {};
            const pagination = {};

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination);
            
            if (response.success) {
                setTimeTracking(response.data);
            }
        } catch (error) {
            console.error('Error fetching time tracking:', error);
        }
    };

    // Fetch productivity metrics
    const fetchProductivityMetrics = async (employeeId) => {
        try {
            const tableSchema = '';
            const tableName = 'user_productivity_metrics';
            const conditions = { employee_id: employeeId };
            const orderBy = 'date DESC';
            const search = {};
            const pagination = {};

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination);
            
            if (response.success) {
                setProductivityMetrics(response.data);
            }
        } catch (error) {
            console.error('Error fetching productivity metrics:', error);
        }
    };

    // Handle employee selection
    const handleEmployeeClick = (employee) => {
        setSelectedEmployee(employee);
        setShowActivityModal(true);
    };

    // Filter functions
    const filteredEmployees = useMemo(() => {
        if (!search.trim()) return employeesList;
        
        return employeesList.filter(employee => {
            const staffName = `${employee.staff?.[0]?.first_name || ''} ${employee.staff?.[0]?.last_name || ''}`.toLowerCase();
            const employeeId = employee.employee_id?.toLowerCase() || '';
            const department = employee.department?.toLowerCase() || '';
            const designation = employee.designation?.toLowerCase() || '';
            
            const searchTerm = search.toLowerCase();
            
            return staffName.includes(searchTerm) || 
                   employeeId.includes(searchTerm) || 
                   department.includes(searchTerm) || 
                   designation.includes(searchTerm);
        });
    }, [employeesList, search]);

    // Utility functions
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return new Date(timeString).toLocaleTimeString('en-IN');
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '0m';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    const getActivityTypeBadge = (type) => {
        const typeConfig = {
            login: { variant: 'success', text: 'Login' },
            logout: { variant: 'danger', text: 'Logout' },
            break_start: { variant: 'warning', text: 'Break Start' },
            break_end: { variant: 'info', text: 'Break End' },
            application_switch: { variant: 'primary', text: 'App Switch' },
            idle: { variant: 'secondary', text: 'Idle' },
            active: { variant: 'success', text: 'Active' }
        };
        
        const config = typeConfig[type] || typeConfig.active;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getProductivityScore = (score) => {
        if (score >= 80) return { variant: 'success', text: 'Excellent' };
        if (score >= 60) return { variant: 'warning', text: 'Good' };
        if (score >= 40) return { variant: 'info', text: 'Average' };
        return { variant: 'danger', text: 'Poor' };
    };

    // Calculate today's summary
    const getTodaySummary = () => {
        if (!selectedEmployee) return null;
        
        const today = new Date().toISOString().split('T')[0];
        const todayTimeTracking = timeTracking.filter(t => t.date === today);
        const todayActivity = activityLogs.filter(a => a.created_at?.split('T')[0] === today);
        
        const totalWorkTime = todayTimeTracking.reduce((sum, t) => sum + (parseInt(t.total_work_time) || 0), 0);
        const totalBreakTime = todayTimeTracking.reduce((sum, t) => sum + (parseInt(t.total_break_time) || 0), 0);
        const totalIdleTime = todayTimeTracking.reduce((sum, t) => sum + (parseInt(t.total_idle_time) || 0), 0);
        const totalScreenshots = screenshots.filter(s => s.captured_at?.split('T')[0] === today).length;
        
        return {
            totalWorkTime,
            totalBreakTime,
            totalIdleTime,
            totalScreenshots,
            loginCount: todayActivity.filter(a => a.activity_type === 'login').length,
            logoutCount: todayActivity.filter(a => a.activity_type === 'logout').length
        };
    };

    const todaySummary = getTodaySummary();

    return (
        <div className="dc-haslayout dc-dbsectionspace">
            <div className="container-fluid py-3">
                <div className="card mb-5">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">User Activity Tracking</h5>
                                <Badge bg="info">Employee Monitoring</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="p-0" style={{ maxWidth: 400, flex: "1 1 auto" }}>
                                    <Form.Control
                                        placeholder="Search employees..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        type="date"
                                        value={dateFilter}
                                        onChange={(e) => setDateFilter(e.target.value)}
                                        style={{ width: 'auto' }}
                                    />
                                    <Form.Select
                                        value={timeRange}
                                        onChange={(e) => setTimeRange(e.target.value)}
                                        style={{ width: 'auto' }}
                                    >
                                        <option value="today">Today</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="custom">Custom</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-primary">{employeesList.length}</h3>
                                <p className="mb-0">Total Employees</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-success">
                                    {employeesList.filter(emp => emp.status === 'active').length}
                                </h3>
                                <p className="mb-0">Active Employees</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-info">
                                    {activityLogs.filter(log => log.created_at?.split('T')[0] === new Date().toISOString().split('T')[0]).length}
                                </h3>
                                <p className="mb-0">Today's Activities</p>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <h3 className="text-warning">
                                    {screenshots.filter(s => s.captured_at?.split('T')[0] === new Date().toISOString().split('T')[0]).length}
                                </h3>
                                <p className="mb-0">Today's Screenshots</p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Employees Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">Loading employees...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th>Employee ID</th>
                                            <th>Employee</th>
                                            <th>Department</th>
                                            <th>Designation</th>
                                            <th>Status</th>
                                            <th>Last Activity</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEmployees.length > 0 ? (
                                            filteredEmployees.map((employee, index) => (
                                                <tr key={employee.id}>
                                                    <td>
                                                        <span className="fw-bold text-primary">
                                                            {employee.employee_id}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <ImageShow
                                                                url={employee.staff?.[0]?.profile_photo_url}
                                                                width={40}
                                                                height={40}
                                                                style={{
                                                                    borderRadius: '50%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                            <div width="100%" style={{ marginLeft: 10 }}>
                                                                <div className="fw-bold">
                                                                    {employee.staff?.[0]?.first_name} {employee.staff?.[0]?.last_name}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {employee.staff?.[0]?.email}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark">
                                                            {employee.department}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold">
                                                            {employee.designation}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Badge bg={employee.status === 'active' ? 'success' : 'secondary'}>
                                                            {employee.status === 'active' ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            {formatTime(employee.last_activity) || 'No activity'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            onClick={() => handleEmployeeClick(employee)}
                                                            title="View Activity"
                                                        >
                                                            <i className="fas fa-chart-line"></i> View Activity
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <Alert variant="info">
                                                        No employees found. 
                                                        {search && ' Try adjusting your search criteria.'}
                                                        {!search && ' No employees available.'}
                                                    </Alert>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Activity Modal */}
                <Modal show={showActivityModal} onHide={() => setShowActivityModal(false)} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Activity Tracking - {selectedEmployee?.staff?.[0]?.first_name} {selectedEmployee?.staff?.[0]?.last_name}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedEmployee && (
                            <Tabs
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                className="mb-4"
                                style={{ gap: 10 }}
                            >
                                <Tab eventKey="overview" title="Overview">
                                    {/* Today's Summary */}
                                    {todaySummary && (
                                        <Row className="mb-4">
                                            <Col md={6} className="mb-3">
                                                <Card className="text-center">
                                                    <Card.Body>
                                                        <h4 className="text-primary">{formatDuration(todaySummary.totalWorkTime)}</h4>
                                                        <p className="mb-0">Work Time</p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="text-center">
                                                    <Card.Body>
                                                        <h4 className="text-warning">{formatDuration(todaySummary.totalBreakTime)}</h4>
                                                        <p className="mb-0">Break Time</p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="text-center">
                                                    <Card.Body>
                                                        <h4 className="text-danger">{formatDuration(todaySummary.totalIdleTime)}</h4>
                                                        <p className="mb-0">Idle Time</p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Card className="text-center">
                                                    <Card.Body>
                                                        <h4 className="text-info">{todaySummary.totalScreenshots}</h4>
                                                        <p className="mb-0">Screenshots</p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    )}

                                    {/* Productivity Chart Placeholder */}
                                    <Card className="mb-3" >
                                        <Card.Header>
                                            <h6 className="mb-0">Productivity Trend</h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="text-center py-4">
                                                <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
                                                <p className="text-muted">Productivity chart will be displayed here</p>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Tab>

                                <Tab eventKey="screenshots" title="Screenshots">
                                    <div className="row">
                                        {screenshots.length > 0 ? (
                                            screenshots.map((screenshot, index) => (
                                                <Col md={4} key={screenshot.id} className="mb-3">
                                                    <Card>
                                                        <Card.Img 
                                                            variant="top" 
                                                            src={screenshot.screenshot_url} 
                                                            style={{ height: '200px', objectFit: 'cover' }}
                                                        />
                                                        <Card.Body>
                                                            <Card.Text>
                                                                <small className="text-muted">
                                                                    {formatTime(screenshot.captured_at)}
                                                                </small>
                                                            </Card.Text>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => window.open(screenshot.screenshot_url, '_blank')}
                                                            >
                                                                View Full Size
                                                            </Button>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            ))
                                        ) : (
                                            <Col md={12}>
                                                <Alert variant="info">
                                                    No screenshots available for the selected period.
                                                </Alert>
                                            </Col>
                                        )}
                                    </div>
                                </Tab>

                                <Tab eventKey="timeline" title="Activity Timeline">
                                    <div className="table-responsive">
                                        <Table hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Time</th>
                                                    <th>Activity</th>
                                                    <th>Application</th>
                                                    <th>Duration</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activityLogs.length > 0 ? (
                                                    activityLogs.map((log, index) => (
                                                        <tr key={log.id}>
                                                            <td>
                                                                <span className="text-muted">
                                                                    {formatTime(log.created_at)}
                                                                </span>
                                                            </td>
                                                            <td>{getActivityTypeBadge(log.activity_type)}</td>
                                                            <td>
                                                                <span className="fw-bold">
                                                                    {log.application_name || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="text-muted">
                                                                    {formatDuration(log.duration)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <Badge bg={log.is_productive ? 'success' : 'warning'}>
                                                                    {log.is_productive ? 'Productive' : 'Non-Productive'}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-4">
                                                            <Alert variant="info">
                                                                No activity logs available for the selected period.
                                                            </Alert>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab>

                                <Tab eventKey="productivity" title="Productivity Metrics">
                                    <div className="table-responsive">
                                        <Table hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Productivity Score</th>
                                                    <th>Work Time</th>
                                                    <th>Break Time</th>
                                                    <th>Idle Time</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {productivityMetrics.length > 0 ? (
                                                    productivityMetrics.map((metric, index) => {
                                                        const scoreConfig = getProductivityScore(metric.productivity_score);
                                                        return (
                                                            <tr key={metric.id}>
                                                                <td>
                                                                    <span className="text-muted">
                                                                        {formatDate(metric.date)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <ProgressBar 
                                                                            now={metric.productivity_score} 
                                                                            variant={scoreConfig.variant}
                                                                            style={{ width: '100px', marginRight: '10px' }}
                                                                        />
                                                                        <span className="fw-bold">
                                                                            {metric.productivity_score}%
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="text-success">
                                                                        {formatDuration(metric.total_work_time)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="text-warning">
                                                                        {formatDuration(metric.total_break_time)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className="text-danger">
                                                                        {formatDuration(metric.total_idle_time)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <Badge bg={scoreConfig.variant}>
                                                                        {scoreConfig.text}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4">
                                                            <Alert variant="info">
                                                                No productivity metrics available for the selected period.
                                                            </Alert>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab>
                            </Tabs>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowActivityModal(false)}>
                            Close
                        </Button>
                        <Button variant="primary" onClick={() => window.print()}>
                            <i className="fas fa-print"></i> Print Report
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default UserActivityTracking;
